import React, { useState, useEffect } from "react";
import axios from "axios";
import Navbar from "../Navbar";
import Footer from "../Footer";
import * as XLSX from "xlsx";
import SalesLineChart from "./LinesChart";
import { FaFileExport } from "react-icons/fa6";
import {
    IoWarningOutline,
    IoInformationCircleOutline,
} from "react-icons/io5";
import { ImSpinner2 } from "react-icons/im";
import OutletSearch from "../SelectSearch/OutletSearch";
import { toast } from "react-toastify";
import ReportTableComponent from "./ReportTableComponent";

const ReportTable = () => {
    const [data, setData] = useState({
        dailyData: [],
        totalData: {},
    });
    const today = new Date().toLocaleDateString('en-CA')
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);

    const [selectedOutlet, setSelectedOutlet] = useState("");

    const token = localStorage.getItem('token');
    const company = localStorage.getItem('selectedCompany');

    useEffect(() => {
        if (selectedOutlet && token) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOutlet, token]);

    const fetchData = async () => {
        if (!selectedOutlet) {
            toast.error("Please select an outlet before searching.");
            return;
        }

        if (!startDate || !endDate) {
            toast.error("Please select a valid date.");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/dailyreport`,
                {
                    outlet: selectedOutlet,
                    dateStart: startDate,
                    dateEnd: endDate,
                    token: token,
                }
            );

            setData({
                dailyData: response.data.daily_data,
                totalData: response.data.total_data,
            });
        } catch (err) {
            setError("Failed to fetch data. Please try again.");
            console.error("Error fetching data:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        if (!selectedOutlet) {
            setError("Please select an outlet before searching.");
            return;
        }

        fetchData();
    };

    const exportToExcel = () => {
        const headers = [
            "Date",
            "Total Sales",
            "VAT",
            "Net Sales",
            "Cash",
            "Mobile Payment",
            "Credit",
            "Credit Card",
            "Split",
            'Discount Amount',
            "Start Bill",
            "End Bill",
            "Complimentary",
            "NonChargeable",
            "Total Guests",
            "Food Sales",
            "Beverage Sales",
            "Other Sales"
        ];

        const exportData = data.dailyData.map((row) => [
            row.Date,
            row.TOTALSALES,
            row.TotalVat,
            row.netTOTALSALES,
            row.paymentStats?.Cash,
            row.paymentStats?.MobilePayment,
            row.paymentStats?.Credit,
            row.paymentStats?.CreditCard,
            row.paymentStats?.Split,
            row.DiscountAmt,
            row.StartBill,
            row.EndBill,
            row.paymentStats?.Complimentary,
            row.paymentStats?.NonChargeable,
            row.TotalGuests,
            row.Food_Sales,
            row.Beverage_Sales,
            row.Others_Sales || 0
        ]);

        exportData.push([
            "Total",
            data.totalData.TOTALSALES,
            data.totalData.TotalVat,
            data.totalData.netTOTALSALES,
            data.totalData.Cash,
            data.totalData.MobilePayment,
            data.totalData.Credit,
            data.totalData.CreditCard,
            data.totalData.Split,
            data.totalData.DiscountAmt,
            data.totalData.min_startbill,
            data.totalData.max_endbill,
            data.totalData.Complimentary,
            data.totalData.NonChargeable,
            data.totalData.TotalGuests,
            data.totalData.Food_Sales,
            data.totalData.Beverage_Sales,
            data.totalData.Others_Sales
        ]);

        const header = [
            [company || "Company Name"],
            [selectedOutlet || "Outlet Name"],
            [`Sales Report from ${startDate} to ${endDate}`],
            [],
            ...[headers],
            [],
        ];

        const finalData = [...header, ...exportData];

        const ws = XLSX.utils.aoa_to_sheet(finalData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");

        XLSX.writeFile(wb, "sales_report.xlsx");
    };

    const formatCurrency = (value) => {
        if (!value || isNaN(value)) return "0.00";
        return new Intl.NumberFormat("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(value);
    };

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />
            <div className="flex-grow">
                <div className="px-2">
                    <div className="w-full max-w-4xl mx-auto my-3 space-y-3">
                        <div className="flex justify-between items-center text-sm">
                            <div className="rounded-[8px] border border-[#E0DDD8] bg-white px-3 py-1.5 flex gap-2 items-center">
                                <label htmlFor="startDate" className="text-sm font-medium text-slate-700 whitespace-nowrap">
                                    Start Date:
                                </label>
                                <input
                                    type="date"
                                    id="startDate"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="border-none outline-none bg-transparent text-[#2C2C2A] font-medium w-[104px] cursor-pointer"
                                />
                            </div>

                            <OutletSearch
                                setSelectedOutlet={setSelectedOutlet}
                                selectedOutlet={selectedOutlet}
                                token={token}
                                compact={true}
                            />

                            <div className="rounded-[8px] border border-[#E0DDD8] bg-white px-3 py-1.5 flex gap-2 items-center">
                                <label htmlFor="endDate" className="text-sm font-medium text-slate-700 whitespace-nowrap">
                                    End Date:
                                </label>
                                <input
                                    type="date"
                                    id="endDate"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="border-none outline-none bg-transparent text-[#2C2C2A] font-medium w-[104px] cursor-pointer"
                                />
                            </div>
                        </div>

                        <div className="flex justify-between items-center">
                            <div className="flex-1 flex justify-start"></div>

                            <div className="flex-1 flex justify-center">
                                <button
                                    onClick={handleSearch}
                                    disabled={loading}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md font-medium bg-violet-100 text-violet-800 hover:bg-violet-200 hover:shadow-md transition-all"
                                >
                                    {loading ? "Loading..." : "Search"}
                                </button>
                            </div>

                            <div className="flex-1 flex justify-end">
                                <button
                                    onClick={exportToExcel}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 hover:shadow-md transition-all"
                                >
                                    <FaFileExport className="h-3 w-3" />
                                    Export
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="text-center mb-2.5 font-semibold space-y-0.5">
                        <div className="text-2xl underline">
                            {company || "Company Name"}
                        </div>
                        <div className="text-lg mb-0.5">
                            {selectedOutlet || "Outlet Name"}
                        </div>
                        <div className="text-base italic">
                            Sales Report from {startDate} to {endDate}
                        </div>
                    </div>

                    {error && (
                        <div className="flex items-center justify-center gap-2 py-6 text-red-600">
                            <IoWarningOutline className="h-6 w-6 flex-shrink-0" />
                            <span className="text-base font-semibold">
                                {error}
                            </span>
                        </div>
                    )}

                    {loading && (
                        <div className="flex items-center justify-center gap-3 py-8 text-gray-600">
                            <ImSpinner2 className="h-5 w-5 animate-spin" />
                            <span className="text-sm font-medium">
                                Loading data...
                            </span>
                        </div>
                    )}

                    {!loading && data.dailyData.length === 0 && (
                        <div className="flex items-center justify-center gap-2 py-8 text-gray-500">
                            <IoInformationCircleOutline className="h-6 w-6 flex-shrink-0" />
                            <span className="text-base font-medium">
                                No Data Available
                            </span>
                        </div>
                    )}

                    {data.dailyData.length > 0 && !loading && !error && (
                        <>
                            <ReportTableComponent
                                data={data}
                                formatCurrency={formatCurrency}
                            />

                            <SalesLineChart data={data} />
                        </>
                    )}
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default ReportTable;